import { User as UserEntity } from '@/core/api/entities/user'

import {
	SUPER_ADMINISTRATOR_ROLE,
	SUPERADMIN_DISPLAY_ROLE,
} from './Users.constants'
import { User } from './Users.types'

/**
 * Maps UserEntity to User interface for UI display
 */
export const mapUserEntityToUser = (userEntity: UserEntity): User => {
	return {
		id: userEntity.id.toString(),
		name: userEntity.getFullName(),
		firstname: userEntity.firstname || '',
		lastname: userEntity.lastname || '',
		email: userEntity.email,
		subaccount: userEntity.account?.name || 'N/A',
		accountName: userEntity.account?.name || 'N/A',
		accountId: userEntity.accountId,
		roleId: userEntity.roleId,
		roles: userEntity.roles,
		account: userEntity.account
			? {
					id: userEntity.account.id,
					name: userEntity.account.name,
				}
			: undefined,
	}
}

/**
 * Gets the primary role name for display
 */
export const getUserRoleName = (user: User): string => {
	return user.roles?.data[0]?.public_name || ''
}

const normalizeRoleName = (value: string): string =>
	value
		.normalize('NFKD')
		.replace(/\u00A0/g, ' ')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.replace(/\s+/g, ' ')

/**
 * Checks whether a role name represents the Super Admin role.
 *
 * This is intentionally resilient to minor formatting differences coming
 * from the API (e.g. whitespace, non-breaking spaces) and to the shortened
 * label variant used in the UI.
 */
export const isSuperAdminRoleName = (roleName: string): boolean => {
	const normalized = normalizeRoleName(roleName)
	return (
		normalized === normalizeRoleName(SUPER_ADMINISTRATOR_ROLE) ||
		normalized === 'superadmin' ||
		normalized === 'super admin' ||
		normalized.includes('super administr')
	)
}

export const getRoleDisplayName = (roleName: string): string =>
	isSuperAdminRoleName(roleName) ? SUPERADMIN_DISPLAY_ROLE : roleName

/**
 * Formats user full name for display
 */
export const formatUserName = (user: User): string => {
	return `${user.firstname} ${user.lastname}`.trim()
}
